<template>
  <CCard>
    <CCardHeader class="vertical-center d-inline-flex flex-gap-2">
      <h5 class="mb-0">
        {{ $route.meta.label }}
        <template v-if="form?.id && !spinners.loading">
          : <h3 class="d-inline"><CBadge color="secondary">#{{ form.id }}</CBadge></h3>
        </template>
      </h5>

      <div class="ml-auto">
        <CButton color="dark" :to="{ name: 'Merchandise Orders' }">
          <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <form-summary
        v-if="!isValid"
        class="form-errors alert alert-danger"
        :validator="$v"
        :attributes="attributes"
      />

      <CForm class="merchandise-order-form" v-if="!spinners.loading">
        <div class="d-flex flex-column">
          <!-- Merchant -->
          <div class="row">
            <div class="col-12">
              <div class="form-group w-100">
                <label class="d-block">Merchant:</label>
                <v-select
                  placeholder="Select restaurant.."
                  v-model="$v.form.merchant_id.$model"
                  :options="allRestaurants"
                  :loading="spinners.loading && allRestaurants.length <= 0"
                  :reduce="option => option.value"
                />
              </div>
            </div>
          </div>

          <!-- Order Type Toggle (Regular Order / Credit Note) -->
          <div class="row">
            <div class="col-12">
              <div class="form-group w-100 order-type-toggle">
                <label class="d-block">Type:</label>
                <div class="d-flex flex-wrap align-items-center flex-gap-2">
                  <CButton
                    size="lg"
                    :color="!form.is_credit_note ? 'info' : 'secondary'"
                    :variant="!form.is_credit_note ? '' : 'outline'"
                    @click="form.is_credit_note = false"
                  >
                    <span>Regular Order</span>
                  </CButton>
                  <CButton
                    size="lg"
                    :color="form.is_credit_note ? 'info' : 'secondary'"
                    :variant="form.is_credit_note ? '' : 'outline'"
                    @click="form.is_credit_note = true"
                  >
                    <span>Credit Note</span>
                  </CButton>
                </div>
              </div>
            </div>
          </div>

          <!-- Order Details -->
          <div class="row">
            <!-- Left Column: Order Date & Notes -->
            <div class="col-12 col-lg-6">
              <CInput
                type="date"
                class="w-100 mb-3"
                label="Order Date:"
                :value.sync="$v.form.order_date.$model"
              />
              <div class="form-group w-100">
                <CTextarea
                  class="mb-0"
                  label="Notes:"
                  placeholder="Additional notes about the order"
                  rows="5"
                  :value.sync="$v.form.description.$model"
                />
              </div>
            </div>

            <!-- Right Column: Total Amount & Tax Rates -->
            <div class="col-12 col-lg-6">
              <!-- Total Amount Display -->
              <div class="total-amount-card mb-3">
                <div class="total-amount-header">
                  <span class="total-amount-label">Total Amount</span>
                </div>
                <div class="total-amount-value">
                  {{ totalAmount | toCurrency }}
                </div>
              </div>

              <!-- Amounts by Tax Rate -->
              <div class="form-group w-100 mb-0">
                <label>Amounts by Tax Rate:</label>
                <div
                  v-for="(item, index) in tax_breakdown"
                  :key="index"
                  class="mb-2 d-flex flex-gap-2 align-items-center"
                >
                  <v-select
                    class="w-100 mb-0"
                    placeholder="Tax Rate (%)"
                    v-model.number="item.rate"
                    :options="taxRates"
                    :reduce="option => option.value"
                  >
                    <template #option="{ label, value }">
                      {{ label }}
                    </template>
                  </v-select>

                  <CInput
                    type="number"
                    class="w-100 mb-0"
                    placeholder="Amount"
                    v-model.number="item.amount"
                    step="0.01"
                    min="0"
                  >
                    <template #prepend-content><CIcon name="cil-euro"/></template>
                  </CInput>
                  <CButton size="sm" shape="pill" color="danger" @click="removeTaxRow(index)" :disabled="tax_breakdown.length <= 1">
                    <CIcon name="cil-trash" />
                  </CButton>
                </div>
                <CButton color="info" size="sm" shape="pill" @click="addTaxRow" variant="ghost">
                  <CIcon name="cil-plus" /> Add Amount
                </CButton>
              </div>
            </div>
          </div>
        </div>
      </CForm>

      <CSpinner v-else color="primary" />
    </CCardBody>

    <CCardFooter class="sticky-bottom">
      <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
        <CButton
          color="primary"
          :disabled="spinners.loading || spinners.btnSubmit || !form.merchant_id"
          @click="submit()"
        >
          <CSpinner v-if="spinners.btnSubmit" size="sm" /> Submit
        </CButton>
      </div>
    </CCardFooter>
  </CCard>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import { validationMixin } from "vuelidate"
import { required, minValue, requiredIf } from "vuelidate/lib/validators"
import _debounce from 'lodash/debounce';

export default {
  name: 'MerchandiseOrder',
  mixins: [validationMixin],
  data() {
    return {
      form: {
        id: null,
        merchant_id: null,
        merchant_type: 'App\\Models\\Restaurant',
        order_date: null,
        amounts_by_tax_rate: {},
        description: null,
        is_credit_note: false
      },
      tax_breakdown: [
        { rate: 21, amount: 0 }
      ],

      spinners: {
        loading: true,
        btnSubmit: false
      },

      unsavedChanges: false,

      // Vuelidate error attributes
      attributes: {
        merchant_id: 'Merchant',
        order_date: 'Order date',
        'tax_breakdown.rate': 'Tax rate',
        'tax_breakdown.amount': 'Amount',
        is_credit_note: 'Is credit note'
      }
    }
  },

  validations: {
    form: {
      merchant_id: { required },
      merchant_type: { },
      order_date: { required },
      amounts_by_tax_rate: {},
      description: { },
      is_credit_note: { },
    },
    tax_breakdown: {
      $each: {
        rate: { required, minValue: minValue(0) },
        amount: { required, minValue: minValue(0) }
      }
    }
  },

  computed: {
    ...mapGetters(["errors"]),
    ...mapGetters('restaurants', ['allRestaurants']),

    taxRates() {
      return [
        { label: '21%', value: 21 },
        { label: '12%', value: 12 },
        { label: '6%', value: 6 },
        { label: '0%', value: 0 }
      ]
    },

    isValid() { return !this.$v.form.$invalid && !this.$v.tax_breakdown.$invalid },
    isDirty() { return this.$v.form.$anyDirty || this.$v.tax_breakdown.$anyDirty },
    itemId() { return Number(this.$route.params.id) || null },
    transformedForm() {
      const { merchant, invoice, ...rest } = this.form;
      rest.amounts_by_tax_rate = this.tax_breakdown.reduce((acc, item) => {
        if (item.rate !== null && item.rate !== '') {
          // Aynı vergi oranındaki tutarları topla
          const existingAmount = acc[item.rate] || 0;
          acc[item.rate] = existingAmount + (parseFloat(item.amount) || 0);
        }
        return acc;
      }, {});

      return {
        ...rest
      }
    },
    totalAmount() {
      return this.tax_breakdown.reduce((total, item) => total + (parseFloat(item.amount) || 0), 0);
    }
  },

  async mounted() {
    // F5 veya başka bir siteye gitmeden önce 'Yaptığınız değişiklikler kaydedilmemiş olabilir.' koruması için
    window.onbeforeunload = () => this.unsavedChanges === true ? true : null;

    this.$watch('form', { handler: 'watchForm', deep: true });

    await this.fetchAllRestaurants();

    if (this.itemId) {
      await this.getForm(this.itemId);
    }

    setTimeout(() => {
      sessionStorage.setItem(`merchandise-order-form`, JSON.stringify(this.transformedForm));
      this.unsavedChanges = false;
      this.spinners.loading = false;
    }, 500);
  },

  methods: {
    ...mapActions('restaurants', ['fetchAllRestaurants']),

    addTaxRow() {
      this.tax_breakdown.push({ rate: 21, amount: 0 });
    },
    removeTaxRow(index) {
      if (this.tax_breakdown.length > 1) {
        this.tax_breakdown.splice(index, 1);
      }
    },

    checkIfValid (fieldName) {
      const fields = fieldName.split(".");
      if (fields[0] === 'tax_breakdown') {
        const field = this.$v.tax_breakdown.$each[fields[1]][fields[2]];
        if (!field.$dirty) return null;
        return !field.$invalid;
      }

      const field = fields.length > 1
        ? this.$v.form[fields[0]][fields[1]]
        : this.$v.form[fieldName];

      if (!field.$dirty) {
        return null
      }
      return !(field.$invalid || field.$model === "");
    },

    validate() {
      this.$v.$touch()
    },

    watchForm(newV, oldV) {
      const initialState = sessionStorage.getItem(`merchandise-order-form`);
      const newState = JSON.stringify(this.transformedForm);

      this.unsavedChanges = initialState !== newState;
    },

    async getForm(id) {
      this.spinners.loading = true;

      try {
        const { data } = await this.$axios.get(
          this.$backend.MERCHANDISE_ORDERS.SHOW.replace("{id}", id)
        );

        const order = data.data;

        this.form = {
          id: order.id,
          merchant_id: order.merchant_id,
          merchant_type: order.merchant_type,
          order_date: order.order_date,
          amounts_by_tax_rate: order.amounts_by_tax_rate || {},
          description: order.description,
          is_credit_note: Boolean(order.is_credit_note)
        };

        if (order.amounts_by_tax_rate && Object.keys(order.amounts_by_tax_rate).length > 0) {
          this.tax_breakdown = Object.entries(order.amounts_by_tax_rate).map(([rate, amount]) => ({
            rate: Number(rate),
            amount: Number(amount)
          }));
        } else {
          this.tax_breakdown = [{ rate: 21, amount: 0 }];
        }
      } catch (error) {
        this.$toast.error(error.response.data.message);
        setTimeout(() => { this.$router.push({ name: 'Merchandise Orders' }) }, 3000);
      } finally {
        this.spinners.loading = false;
        this.unsavedChanges = false;
      }
    },

    async submit() {
      if (!this.isValid) {
        this.$v.$touch();
        return;
      }

      this.spinners.btnSubmit = true;

      try {
        if (this.itemId) {
          await this.update();
        } else {
          await this.store();
        }
      } finally {
        this.spinners.btnSubmit = false;
      }
    },

    async store() {
      try {
        const { data } = await this.$axios.post(
          this.$backend.MERCHANDISE_ORDERS.STORE,
          this.transformedForm
        );

        this.$toast.success('Merchandise order created successfully');
        this.unsavedChanges = false;

        setTimeout(() => { this.$router.push({ name: 'Merchandise Orders' }); }, 2000);
      } catch (error) {
        this.$toast.error(
          typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : this.errors
        );
      }
    },

    async update() {
      try {
        const { data } = await this.$axios.put(
          this.$backend.MERCHANDISE_ORDERS.UPDATE.replace("{id}", this.itemId),
          this.transformedForm
        );

        this.$toast.success('Merchandise order updated successfully');
        this.unsavedChanges = false;
      } catch (error) {
        this.$toast.error(
          typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : this.errors
        );
      }
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.unsavedChanges === true) {
      const answer = window.confirm('Do you really want to leave? You have unsaved changes!')
      this.unsavedChanges = !answer;
      next(answer) // answer: true or false
    } else {
      next()
    }
  }
}
</script>

<style>
.merchandise-order-form .form-control,
.merchandise-order-form .vs__search,
.merchandise-order-form .vs__dropdown-menu,
.merchandise-order-form .vs__selected {
  font-size: 1.125rem;
  color: #9b0a0a;
  font-weight: 500;
}

.merchandise-order-form .vs__dropdown-toggle {
  padding: 2px 0 6px;
}

.total-amount-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 0.375rem;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 1rem;
}

.total-amount-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.total-amount-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  gap: 0.5rem;
}

.total-amount-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-amount-value {
  font-size: 1.6rem;
  font-weight: 600;
  color: #28a745;
  line-height: 1;
}

@media (max-width: 991.98px) {
  .total-amount-value {
    font-size: 1.4rem;
  }
  
  .total-amount-card {
    margin-top: 0.675rem;
    height: 80px;
  }
}

@media (max-width: 575.98px) {
  .total-amount-value {
    font-size: 1.4rem;
  }
  
  .total-amount-card {
    height: 80px;
  }
}

.order-type-toggle {
  span {
    font-weight: 500;
  }
}
</style>
